import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from '@openng/optimus-ui/select';
import { Subject, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../common/BaseComponent';
import { GlobalTemplateService } from '../../../../../services/global-template.service';
import { ToastBarService } from '../../../../../services/toast-bar.service';
import { VariableMaxLengthService } from '../../../../../services/variable-max-length.service';
import {
  MetaSingleImageAccountTypeInitModel,
  MetaSingleImageAccountTypeInitVariables,
  MetaSingleImageAccountTypeModel,
  MetaSingleImageAccountTypeVariables,
} from '../../../../../models/meta-template/meta-single-image-account-type.interface';
import { META_ACCOUNT_TYPES } from '../../../../../utils/meta-cta-helper';
import { Helper } from '../../../../../utils/helper';

/**
 * Picks the Meta account (Facebook or Instagram) the ad runs from. The label
 * is stored verbatim under `AccountType`.
 */
@Component({
  selector: 'app-meta-single-image-account-type',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule],
  templateUrl: './meta-single-image-account-type.component.html',
  styleUrl: './meta-single-image-account-type.component.scss',
})
export class MetaSingleImageAccountTypeComponent extends BaseComponent implements OnInit, OnDestroy {
  //@ input
  @Input() block: MetaSingleImageAccountTypeModel = new MetaSingleImageAccountTypeInitModel();
  @Input() isPreview = false;
  //@ output
  @Output() blockData = new EventEmitter<any>(); // Event emitter to send data to the parent
  @Output() removeBlockEvent = new EventEmitter<string | null>(); // New EventEmitter for remove

  // Copied, not aliased: p-select's [options] is typed as a mutable any[],
  // which a readonly array cannot satisfy under strictTemplates.
  readonly accountTypes: string[] = [...META_ACCOUNT_TYPES];
  selectedAccountType: string = '';
  disabled: boolean = true;
  private _destroy$ = new Subject<void>();

  constructor(
    toastBarService: ToastBarService,
    private globalTemplateService: GlobalTemplateService,
    private _variableMaxLengthService: VariableMaxLengthService
  ) {
    super(toastBarService);
  }

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new MetaSingleImageAccountTypeInitVariables();
    }

    const savedAccountType = this.block.variables!['AccountType'] ?? '';
    this.selectedAccountType = this.accountTypes.includes(savedAccountType) ? savedAccountType : '';

    // Disable the dropdown when the template is read-only or locked by another user.
    this.globalTemplateService
      .getIsDisabled()
      .pipe(takeUntil(this._destroy$))
      .subscribe((isDisabled: boolean) => {
        this.disabled = isDisabled;
      });

    // Register the AccountType max length so the docx export prints a value (not "Undefined").
    this._variableMaxLengthService.addSingleVariableMaximumLength({
      name: 'AccountType',
      maxLength: 4000,
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  //@ placeholders
  accountTypePlaceholder: string = 'Select account type';

  //@remove
  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId); // Emit the ID to parent
  }

  //@onChange
  onChange(value: any, fieldName: keyof MetaSingleImageAccountTypeVariables) {
    if (this.block.variables) {
      this.block.variables[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }
}
